import React, { useMemo, useState, useEffect } from "react";
import classNames from "classnames";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import { getActiveColorStyle } from "utils/colorUtils";
import { getSelectStyles } from "utils/selectStyles";
import { formatDateForDisplay } from "utils/dateUtils";
import { formatPriceWithDollar } from "utils/currency";
import Select from "components/Select";
import WorksheetForm from "views/components/Deals/WorksheetForm";
import { useCreateWorksheet } from "api/worksheet/service";
import { useDeleteWorksheet } from "api/worksheet/service";
import { useGetChannels } from "api/channels/service";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import { useNotification } from "contexts/NotificationContext";
import SecureMediaImg from "components/SecureMediaImg";

const DealWorksheetsTab = ({
  activeColor = "blue",
  data,
  id,
  permission,
  dealId,
  isWorksheetEditing,
  setIsWorksheetEditing,
  selectedWorksheetId,
  setSelectedWorksheetId,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const createWorksheet = useCreateWorksheet();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showLenderSelect, setShowLenderSelect] = useState(false);
  const [selectedLender, setSelectedLender] = useState(null);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const deleteWorksheetMutation = useDeleteWorksheet();
  const { data: lenders, isLoading: lendersLoading } = useGetChannels("finance_company_bank");

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".deal-worksheets-tab.deal-worksheets-tab--theme2",
            fieldsSelector: ".deal-worksheets-tab-fields",
          })
        : "",
    [isTheme2]
  );

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Check URL parameters on component mount to restore edit state
  useEffect(() => {
    const worksheet = searchParams.get('worksheet');
    if (worksheet) {
      setSelectedWorksheetId(worksheet);
      setIsWorksheetEditing(true);
    }
  }, [searchParams]);

  const handleDeleteClick = (documentId) => {
    setSelectedDeleteId(documentId);
    setDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedDeleteId) return;
    deleteWorksheetMutation.mutate({ id: selectedDeleteId, dealId: id }, {
      onSettled: () => {
        setDeleteModalOpen(false);
        setSelectedDeleteId(null);
      }
    });
  };

  const getStatusBadgeColor = (status) => {
    // Mirror SearchDeals coloring
    if (status === 'working') return 'success';
    if (status === 'prospect') return 'info';
    if (status === 'completed') return 'success';
    return 'danger';
  };

  const formatDisplayText = (text) => {
    if (!text) return '';
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Normalize to array; API returns a single worksheet object for this tab
  const worksheets = Array.isArray(data) ? data : (data ? [data] : []);

  const handleStartWorksheet = (worksheetId) => {
    setSelectedWorksheetId(worksheetId);
    setIsWorksheetEditing(true);

    // Update URL with edit parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('worksheet', worksheetId);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleCancelWorksheet = () => {
    setIsWorksheetEditing(false);
    setSelectedWorksheetId(null);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('worksheet');
    setSearchParams(newSearchParams, { replace: true });
  };



  const createWorksheetFn = async (type) => {
    try {
      if (type === "local_lender_finance") {
        setShowLenderSelect(true);
        return;
      }

      const data = {
        deal_id: id,
        type: type,
      }
      createWorksheet.mutate(data);
    } catch (error) {
      console.error("Error creating worksheet:", error);
    }
  };

  const handleLenderSelect = (lender) => {
    setSelectedLender(lender);
  };

  const handleCreateLocalLenderWorksheet = () => {
    if (!selectedLender) return;

    const data = {
      deal_id: id,
      type: "local_lender_finance",
      finance_company: selectedLender.id
    };


    createWorksheet.mutate(data);
    setShowLenderSelect(false);
    setSelectedLender(null);
  };

  const handleCancelLenderSelect = () => {
    setShowLenderSelect(false);
    setSelectedLender(null);
  };

  return (
    <div className={classNames("deal-worksheets-tab", { "deal-worksheets-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddCompany && (
        <ChannelsEmbedded
          activeColor={activeColor}
          defaultType="finance_company_bank"
          onClose={() => setShowAddCompany(false)}
        />
      )}
      {!showAddCompany && (
        <Row>
          <Col md="12">
            <Card>
              {permission?.deals_worksheets_add &&
                !isWorksheetEditing && (
                  <CardHeader>
                    <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                      <>
                        {/* <Button
                          size="sm"
                          className="ml-2"
                          style={{ ...activeColorStyle, color: 'white' }}
                          onClick={() => handleStartWorksheet('cash', 'Cash Worksheet', null)}
                        >
                          Cash Worksheet
                        </Button> */}
                        {/* <Button
                          size="sm"
                          className="ml-2"
                          style={{ ...activeColorStyle, color: 'white' }}
                          onClick={() => handleStartWorksheet('in_house_finance', 'In-House Finance Worksheet', null)}
                        >
                          In-House Finance Worksheet
                        </Button> */}
                        {/* <Button
                          size="sm"
                          className="ml-2"
                          style={{ ...activeColorStyle, color: 'white' }}
                          onClick={() => handleStartWorksheet('local_lender_finance', 'Local Lender Finance Worksheet', null)}
                        >
                          Local Lender Finance Worksheet
                        </Button> */}
                        {!showAddCompany && (
                          <UncontrolledDropdown >
                            <DropdownToggle
                              size="sm"
                              className="ml-2"
                              style={{ ...activeColorStyle, color: 'white' }}
                              caret
                            >
                              {t('dealWorksheetsTab.addNew')}
                            </DropdownToggle>
                            <DropdownMenu right>
                              <DropdownItem
                                onClick={() => createWorksheetFn("local_lender_finance")}
                              >
                                {t('dealWorksheetsTab.localLenderFinanceWorksheet')}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() => createWorksheetFn("in_house_finance")}
                              >
                                {t('dealWorksheetsTab.inHouseFinanceWorksheet')}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() => createWorksheetFn("cash")}
                              >
                                {t('dealWorksheetsTab.cashWorksheet')}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        )}
                      </>
                    </div>
                  </CardHeader>
                )}

              {/* Lender Selection Area */}
              {showLenderSelect && !showAddCompany && (
                <CardBody className={classNames({ "deal-worksheets-tab-fields": isTheme2 })} style={{ alignItems: 'center' }}>
                  <Row>
                    <Col md="12" >
                      <h6 className="mb-3">{t('dealWorksheetsTab.selectLocalLenderForFinanceWorksheet')}</h6>
                      {lendersLoading ? (
                        <div className="text-center">{t('dealWorksheetsTab.loadingLenders')}</div>
                      ) : !lenders?.results || lenders.results.length === 0 ? (
                        <>

                          {!showAddCompany && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                              <h5 className="text-center mb-0">{t('dealWorksheetsTab.noLendersFound')}</h5>
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                  style={{ ...activeColorStyle, color: 'white' }}
                                  onClick={() => setShowAddCompany(true)}
                                >
                                  {t('dealWorksheetsTab.addLender')}
                                </Button>
                              </div>
                            </div>
                          )}
                        </>


                      ) : (
                        <Row style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <Col md="6">
                            <div className="form-group mb-0">
                              <label>{t('dealWorksheetsTab.selectLender')} *</label>
                              <Select
                                styles={selectStyles}
                                className="react-select"
                                placeholder={t('dealWorksheetsTab.selectALender')}
                                name="lender"
                                isSearchable
                                closeMenuOnSelect={true}
                                value={selectedLender ? {
                                  value: selectedLender.documentId || selectedLender.id,
                                  label: selectedLender.name
                                } : null}
                                onChange={(option) => {
                                  const lender = lenders.results.find(l => (l.documentId || l.id) === option?.value);
                                  handleLenderSelect(lender);
                                }}
                                options={[
                                  { value: '', label: t('dealWorksheetsTab.selectALender') },
                                  ...(lenders?.results.map((lender) => ({
                                    value: lender.documentId || lender.id,
                                    label: lender.name
                                  })) || [])
                                ]}
                                isClearable
                                isLoading={lendersLoading}
                              />
                            </div>
                          </Col>
                          <Col md="6" className="d-flex align-items-end">
                            <div className="form-group mb-0">
                              <Button
                                style={{ ...activeColorStyle, color: 'white' }}
                                onClick={handleCreateLocalLenderWorksheet}
                                disabled={!selectedLender}
                                className="mr-2 mb-0"
                              >
                                {t('dealWorksheetsTab.createWorksheet')}
                              </Button>
                              <Button
                                color="secondary"
                                onClick={handleCancelLenderSelect}
                                className="mb-0"
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              )}
              {!showLenderSelect &&
                <CardBody>
                  {isWorksheetEditing ? (
                    <div>
                      <WorksheetForm
                        dealId={dealId}
                        permission={permission}
                        handleCancelWorksheet={handleCancelWorksheet}
                        activeColor={activeColor}
                        isLightTheme={isLightTheme}
                        isTheme2={isTheme2}
                        id={selectedWorksheetId}
                      />
                    </div>
                  ) : (
                    <>
                      {!worksheets || worksheets?.length === 0 ? (
                        <div>{t('dealWorksheetsTab.noWorksheetsFound')}</div>
                      ) : (
                        <Table responsive>
                          <thead className="text-primary">
                            <tr>
                              <th className="text-center">ID</th>
                              <th>{t('dealWorksheetsTab.vehicle')}</th>
                              <th>{t('dealWorksheetsTab.contractDate')}</th>
                              <th>{t('dealWorksheetsTab.salesLocation')}</th>
                              <th>{t('dealWorksheetsTab.salesPrice')}</th>
                              <th>{t('dealWorksheetsTab.totalSale')}</th>
                              <th className="text-center">{t('dealWorksheetsTab.information')}</th>
                              <th className="text-right"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {worksheets?.map((item, idx) => (
                              <tr
                                key={item?.worksheet_id || idx}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  handleStartWorksheet(item?.worksheet_id);
                                }}
                              >
                                <td className="text-center">{item?.worksheet_id}</td>
                                {/* <td>{item?.type || '-'}</td> */}
                                <td>
                                  <div style={{ display: "flex", flexDirection: "row", gap: "5px", alignItems: "center" }}>

                                    <div style={{ width: "100px" }}>
                                      <SecureMediaImg
                                        src={item?.inventory?.vehicle_image || ""}
                                        alt=""
                                        className="card-img-top"
                                        style={{ height: "100px", objectFit: "contain" }}
                                        enabled={!!item?.inventory?.vehicle_image}
                                        fallback={
                                          <img
                                            src={require("assets/img/car.png")}
                                            className="card-img-top"
                                            alt=""
                                            style={{ height: "100px", objectFit: "contain" }}
                                          />
                                        }
                                      />
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                      {item?.inventory?.stock_number &&
                                        <div>
                                          {item?.inventory?.stock_number}
                                        </div>
                                      }
                                      {item?.inventory?.inventory_vehicle?.make &&
                                        <div>
                                          {item?.inventory?.inventory_vehicle?.make + ' ' + (item?.inventory?.inventory_vehicle?.model == null ? '-' : item?.inventory?.inventory_vehicle?.model)}
                                        </div>
                                      }

                                      {item?.inventory?.inventory_vehicle.vin &&
                                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                          <span>{item?.inventory?.inventory_vehicle.vin}</span>
                                          {item?.inventory?.inventory_vehicle?.vin && (
                                            <Button
                                              color="link"
                                              size="sm"
                                              className="p-0"
                                              style={{ cursor: "pointer", flexShrink: 0, lineHeight: 1 }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(item.inventory.inventory_vehicle.vin);
                                                showNotification("success", "VIN copied to clipboard");
                                              }}
                                            >
                                              <i
                                                className="fa fa-copy"
                                                style={{
                                                  fontSize: 13,
                                                  color: isLightTheme ? "#525f7f" : "rgba(255, 255, 255, 0.85)",
                                                }}
                                              />
                                            </Button>
                                          )}
                                        </div>
                                      }
                                    </div>

                                  </div>
                                </td>
                                <td>{formatDateForDisplay(item?.contract_date)}</td>
                                <td>{item?.inventory?.location?.name}</td>
                                <td>{item?.sales_price != null ? formatPriceWithDollar(item.sales_price) : '-'}</td>
                                <td>{item?.washout?.total_sale != null ? formatPriceWithDollar(item.washout.total_sale) : '-'}</td>

                                <td className="text-center">
                                  <Badge color={getStatusBadgeColor(item?.type)}>
                                    {item?.type === "local_lender_finance" ? (item?.finance_company?.name || item?.local_lender?.name) : item?.type === "cash" ? t('dealWorksheetsTab.cash') : t('dealWorksheetsTab.inHouse')}
                                  </Badge>
                                  <Badge color={getStatusBadgeColor(item?.worksheet_status)}>
                                    {formatDisplayText(item?.worksheet_status)}
                                  </Badge>
                                </td>
                                <td className="text-right">
                                  <div className="actions-right">
                                    {permission?.deals_worksheets_edit && (
                                      <Button
                                        color="warning"
                                        size="sm"
                                        className="btn-icon btn-link like"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStartWorksheet(item?.worksheet_id);
                                        }}
                                      >
                                        <i className="tim-icons icon-pencil" />
                                      </Button>
                                    )}
                                    {permission?.deals_worksheets_delete && (
                                      <Button
                                        color="danger"
                                        size="sm"
                                        className="btn-icon btn-link like"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(item?.documentId || idx);
                                        }}
                                      >
                                        <i className="tim-icons icon-simple-remove" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </CardBody>
              }
            </Card>
          </Col>
        </Row>
      )}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('dealWorksheetsTab.deleteWorksheet')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('dealWorksheetsTab.areYouSureDeleteWorksheet')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
          >
            {t('common.cancel')}
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
          >
            {t('dealApplicantTab.yesDelete')}
          </Button>
        </div>
      </Modal>


    </div>
  );
};

export default DealWorksheetsTab;

